import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';

type Theme = 'light' | 'dark' | 'system';

/**
 * SHL-4: light/dark via CSS variables and prefers-color-scheme, persisted.
 * Replaces the vendored jQuery theme panel; "system" is the default and stamps
 * no attribute, letting the media query decide.
 */
export function ThemeToggle() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(() => readTheme());

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem('theme', theme);
    } catch {
      // Blocked storage just means the choice does not persist.
    }
  }, [theme]);

  const next: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' };
  const label: Record<Theme, string> = {
    system: t('common.themeSystem'),
    light: t('common.themeLight'),
    dark: t('common.themeDark'),
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setTheme(next[theme])}
      title={`${t('common.theme')}: ${label[theme]}`}
      aria-label={`${t('common.theme')}: ${label[theme]}`}
    >
      {theme === 'dark' ? '🌙' : theme === 'light' ? '☀️' : '💻'}
    </Button>
  );
}

function readTheme(): Theme {
  try {
    const stored = localStorage.getItem('theme');
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Fall through to the default.
  }
  return 'system';
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

/** Applied before React mounts, so there is no flash of the wrong theme. */
export function initTheme(): void {
  applyTheme(readTheme());
}
